import React, { useState } from 'react';
import { toast } from 'react-toastify';

import { CheckboxTable } from './CheckboxTable';
import { MappedFulfillment } from './Claim/types';
import { ConfirmationModal } from './ConfirmationModal';
import { Dropdown } from './Dropdown';
import { Button } from './buttons/Button';
import { createProductReturn } from '../api';
import { Close } from '../assets/Close';
import { returnReasonOptions } from '../dropdownOptions';
import { Customer, Option } from '../pages/types';
import {
  BOTTOM_TOAST,
  calculateAmountRefundedWithoutTax,
  calculateUnitPriceAfterDiscount,
  getPetNameForFulfillmentProduct,
} from '../utils';

export interface CreateReturnModalProps {
  customer: Customer;
  orderId: string;
  products: MappedFulfillment[];
  onCancel: () => void;
}

export const CreateReturnModal = ({
  customer,
  orderId,
  products,
  onCancel,
}: CreateReturnModalProps) => {
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [selectedReturnReason, setSelectedReturnReason] = useState<Option>({
    value: '',
    label: 'Select reason',
  });
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const isFormComplete = selectedRows.length > 0 && selectedReturnReason.value !== '';

  const mapProductData = (products: MappedFulfillment[]) => {
    return products.map((product) => {
      const petName = getPetNameForFulfillmentProduct(product, customer.pets);
      const priceAfterDiscount = calculateUnitPriceAfterDiscount(product);
      const alreadyRefundedAmount = calculateAmountRefundedWithoutTax(
        priceAfterDiscount,
        product.refund_amount ?? '0.00',
        product.tax_amount
      );
      const availableForRefund = (
        Number(priceAfterDiscount) - Number(alreadyRefundedAmount)
      ).toFixed(2);

      return {
        'Pet Name': petName,
        Quantity: product.quantity,
        'Product Code': product.code,
        'Product Description': product.name,
        'Price Paid without Tax': `$${calculateUnitPriceAfterDiscount(product)}`,
        'Available for Refund': `$${availableForRefund}`,
      };
    });
  };

  const handleCheckboxChange = (index: number) => {
    if (selectedRows.includes(index)) {
      setSelectedRows((prevSelected) => prevSelected.filter((rowIndex) => rowIndex !== index));
    } else {
      setSelectedRows((prevSelected) => [...prevSelected, index]);
    }
  };

  const handleCreateReturn = async (
    selectedRows: number[],
    products: MappedFulfillment[],
    returnReason: string
  ) => {
    setIsLoading(true);
    const productsToReturn = selectedRows.map((rowIndex) => products[rowIndex]);
    // Group by fulfillment id - we should create one return per fulfillment
    const productsToReturnByFulfillment: { [key: string]: MappedFulfillment[] } = {};
    productsToReturn.forEach((product) => {
      if (!productsToReturnByFulfillment[product.fulfillment.id]) {
        productsToReturnByFulfillment[product.fulfillment.id] = [];
      }
      productsToReturnByFulfillment[product.fulfillment.id].push(product);
    });
    try {
      for (const fulfillmentId in productsToReturnByFulfillment) {
        const productsForFulfillment = productsToReturnByFulfillment[fulfillmentId];
        await createProductReturn(fulfillmentId, productsForFulfillment, returnReason);
      }
      toast.success(`Product return${selectedRows.length > 1 ? 's' : ''} initiated`, BOTTOM_TOAST);
      setIsLoading(false);
      setShowConfirmationModal(false);
      onCancel();
      setTimeout(() => window.location.reload(), 3000);
    } catch (error) {
      toast.error(`Failed to create product return: ${error}`, BOTTOM_TOAST);
      setIsLoading(false);
      throw error;
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50">
      {showConfirmationModal && (
        <ConfirmationModal
          title="Are you sure you want to proceed with starting this return?"
          message=""
          customMessage={
            <div className="pb-6 text-lg">
              <p>
                By proceeding, a return shipping label will be automatically emailed to the
                customer.
              </p>
              <br />
              <p>
                Refunds typically appear within 10 business days after we receive their package.
                We'll refund their original form of payment.
              </p>
            </div>
          }
          confirmLabel="Confirm"
          onConfirm={() => {
            handleCreateReturn(selectedRows, products, selectedReturnReason.value);
          }}
          onCancel={() => setShowConfirmationModal(false)}
          isProcessLoading={isLoading}
        />
      )}

      <div className="bg-white rounded-lg shadow-md p-4 w-226 relative border">
        <Close
          className="absolute top-4 right-4 w-8 h-8 cursor-pointer"
          onClick={() => onCancel()}
        />
        <div className="flex flex-col gap-6">
          <div>
            <h3 className="text-2xl font-semibold text-start">Start UnKibble Return</h3>
            <p className="text-lg text-start">
              <span className="font-bold">Customer</span>: {customer.first_name}{' '}
              {customer.last_name} ({customer.email})
            </p>
            <p className="text-lg text-start">
              <span className="font-bold">Order</span>: {orderId}
            </p>
          </div>

          <div>
            <h4 className="text-lg font-bold text-start mb-2">
              Which UnKibble products will be returned?
            </h4>
            <div className="w-full max-w-[903px]">
              <CheckboxTable
                header={[
                  'Pet Name',
                  'Quantity',
                  'Product Code',
                  'Product Description',
                  'Price Paid without Tax',
                  'Available for Refund',
                ]}
                data={mapProductData(products)}
                selectedRows={selectedRows}
                onCheckboxChange={handleCheckboxChange}
              />
            </div>
          </div>

          <div>
            <h4 className="text-lg font-bold text-start mb-2">
              Why is the customer returning this product?
            </h4>
            <div className="w-full">
              <Dropdown
                options={returnReasonOptions}
                selectedOption={selectedReturnReason}
                setSelectedOption={(newSelection) => setSelectedReturnReason(newSelection)}
              />
            </div>
          </div>

          <div className="flex justify-center">
            <Button
              variant="primary"
              label="Start Return"
              isDisabled={!isFormComplete}
              onClick={() => setShowConfirmationModal(true)}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
